import React, { useContext, FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';

// MUI
import Toolbar from '@mui/material/Toolbar';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';

// Custom Components
import useFormController from '@/components/useFormController';
import DynamicDialog from '@/ui/Dialog/DynamicDialog';
import DialogToolbarHeading from '@/ui/Dialog/DialogToolbarHeading';
import DialogToolbarButtonClose from '@/ui/Dialog/ToolbarButtons/DialogToolbarButtonClose';
import ToolbarFillContent from '@/ui/Toolbar/ToolbarFillContent';
import DialogActionButtonAdd from '@/ui/Dialog/ActionButtons/DialogActionButtonAdd';
import DialogActionButtonSave from '@/ui/Dialog/ActionButtons/DialogActionButtonSave';
import DialogContext, { IDialogProps } from '@/context/DialogContext/DialogContext';
import SnackbarContext from '@/context/SnackbarContext/SnackbarContext';
import { GridContainer } from '@/ui/Grid';
import FormContent from '@/components/FormContent';

// Types
import {
  ClosingDetails,
  FormControllerProps,
} from '@/@types/components/formController';
import DialogActionButtonCancel from '@/ui/Dialog/ActionButtons/DialogActionButtonCancel';

const ModelsDialog: FunctionComponent<IDialogProps> = (props) => {
  const { hideDialog } = useContext(DialogContext);
  const { showNotification } = useContext(SnackbarContext);
  const { t } = useTranslation();

  const { mode, onClose } = props;

  const {
    record,
    validation,
    fields,
    pickerDataControllers,
    onFieldChange,
    doInsert,
    doUpdate,
    doClose,
  } = useFormController(props as FormControllerProps);

  const handleFieldChange = (value: any, source: string) => {
    if (onFieldChange) {
      onFieldChange(value, source);
    }
  };

  const handleInsert = () => {
    if (doInsert) {
      doInsert()
        .then((result) => {
          if (result.success) {
            const msg = 'messages.insert_model_success';
            showNotification(msg, 'success');
            close({ dataChanged: true, action: 'insert', id: result.id });
          } else if (result.error) {
            const msg = 'error.insert_model';
            showNotification(msg, 'error');
          } else if (result.validationPass === false) {
            // do nothing
          }
        })
        .catch((resp) => {
          showNotification(resp.error, 'error');
        });
    }
  };

  const handleUpdate = () => {
    if (doUpdate) {
      doUpdate()
        .then((result) => {
          if (result.success) {
            const msg = 'messages.update_model_success';
            showNotification(msg, 'success');
            close({ dataChanged: true, action: 'update' });
          } else if (result.error) {
            const msg = 'error.update_model';
            showNotification(msg, 'error');
          } else if (result.validationPass === false) {
            // do nothing
          }
        })
        .catch((resp) => {
          showNotification(resp.error, 'error');
        });
    }
  };

  const handleClose = () => {
    if (doClose) {
      doClose()
        .then((result) => {
          if (result.success) {
            close({ dataChanged: false, action: 'cancel' });
          }
        })
        .catch((e) => console.log(e));
    }
  };

  // const handleActivate = () => {
  //   if (record.id !== null) {
  //     try {
  //       userService.activate(Number(record.id)).then((result) => {
  //         if (result) {
  //           const msg = t('messages.activate_user_success');
  //           showNotification(msg, 'success');
  //           doRefresh();
  //           onClose({ dataChanged: true, action: 'update' });
  //         } else {
  //           const msg = t('error.activate_user');
  //           showNotification(msg, 'error');
  //         }
  //       });
  //     } catch (error) {
  //       const msg = t('error.activate_user');
  //       showNotification(msg, 'error');
  //     }
  //   }
  // };

  //   const handleDeactivate = () => {
  //   if (record.id !== null) {
  //     try {
  //       userService.deactivate(Number(record.id)).then((result) => {
  //         if (result) {
  //           const msg = t('messages.deactivate_user_success');
  //           showNotification(msg, 'success');
  //           close({ dataChanged: true, action: 'update' });
  //         } else {
  //           const msg = t('error.deactiavate_user');
  //           showNotification(msg, 'error');
  //         }
  //       });
  //     } catch (error) {
  //       const msg = t('error.deactivate_user');
  //       showNotification(msg, 'error');
  //     }
  //   }
  // };

  const close = (result: ClosingDetails) => {
    onClose(result);
    hideDialog();
  };

  // const handleDelete = () => {
  //   if (doDelete) {
  //     doDelete().then((result) => {
  //       if (result.success) {
  //         const msg = t('messages.user_deleted');
  //         showNotification(msg, 'success');
  //         close({ dataChanged: true, action: 'update' });
  //       } else if (result.error) {
  //         const msg = t('error.delete') + result.error;
  //         showNotification(msg, 'error');
  //       }
  //     });
  //   }
  // };

  let cmode = mode;
  if (record.status === 30) {
    cmode = 'view';
  }

  return (
    <DynamicDialog open maxWidth='sm' onClose={handleClose}>
      <DialogTitle>
        <Toolbar variant="dense" disableGutters>
          <DialogToolbarHeading>
            {t('admin:titles.models')}
          </DialogToolbarHeading>
          <ToolbarFillContent />
          <DialogToolbarButtonClose onClick={handleClose} />
        </Toolbar>
      </DialogTitle>
      <DialogContent>
        <GridContainer direction="row" spacing={2} my={2}>
          <FormContent
            title={t('admin:titles.model_information') as string}
            fieldNames={['code', 'opis']}
            record={record}
            validation={validation}
            fields={fields}
            onFieldChange={handleFieldChange}
            mode={cmode}
            columns={2}
            pickerDataControllers={pickerDataControllers}
          />
        </GridContainer>
      </DialogContent>
      <DialogActions>
        <DialogActionButtonCancel variant="outlined" onClick={handleClose} />
        {mode === 'insert' ? (
          <DialogActionButtonAdd variant="contained" onClick={handleInsert} />
        ) : mode === 'update' ? (
          <DialogActionButtonSave variant="contained" onClick={handleUpdate} />
        ) : null}
      </DialogActions>
    </DynamicDialog>
  );
};

export default ModelsDialog;
